import type { QuizPlayer, QuizTeamMember } from 'types/quiz-player';
import type { SanityGytrInsightLines } from '@lib/sanity/types';

export type InsightSignal =
  | 'lowProjectedStarter'
  | 'flaggedStarter'
  | 'strongFallback';

export type ResolvedInsight = {
  signal: InsightSignal;
  text: string;
};

/**
 * Rules-based readout of the weakest true signal in the XI (spec 5.5). The
 * weak-link signal now comes from the server's `hasWeakStarter` flag on the
 * team-rating response: it is `true` when the squad's lowest-projected starter
 * is genuinely weak (below the AI's threshold), and we fall back to an honest
 * neutral line otherwise. Per-player projections no longer reach the client, so
 * the server flag replaces the previous client-side threshold. We have no
 * injury/flag data in the squad payload yet, so the flagged-starter signal is
 * reserved for when that lands.
 */
export const resolveInsightLine = (
  team: QuizTeamMember[],
  hasWeakStarter: boolean,
  lines?: SanityGytrInsightLines
): ResolvedInsight | null => {
  const xi = team
    .slice(0, 11)
    .map((member) => member.player)
    .filter((player): player is QuizPlayer => player !== null);

  if (xi.length === 0) return null;

  const signal: InsightSignal = hasWeakStarter
    ? 'lowProjectedStarter'
    : 'strongFallback';

  const text = lines?.[signal];
  if (!text) return null;

  return { signal, text };
};
